// BHP-1132: the shapes and rules for provisioning a generic VPS from the UI.
//
// The backend contract (BHP-1130 `POST /api/vps`) is the source of truth: the
// caller supplies where to put the server, how big it should be, and in which
// Hetzner project it lives. `hetznerId` and labels are NOT chosen here — they
// come from Hetzner — so this input carries none of them.

/** A choosable (server type, location) combination from `GET /api/server-types`. */
export interface ServerTypeOption {
  /** e.g. "cx22-nbg1"; the `server-type`+`location` pair, used as the option id. */
  identifier: string;
  /** e.g. "cx22" — the Hetzner server type sent to the create call. */
  serverType: string;
  /** e.g. "nbg1" — the Hetzner location sent to the create call. */
  location: string;
  /** Human-readable name from the catalog. */
  name: string;
  /** Longer description from the catalog. */
  description: string;
  /** Monthly price after this customer's margin, net EUR. */
  monthlyPrice: number;
  /**
   * BHP-1146: whether Hetzner can currently create this type in this location.
   * Out-of-stock types are shown but not choosable, never hidden.
   */
  available: boolean;
  /** Why it is unavailable, e.g. "out_of_stock"; null when available. */
  availabilityReason: string | null;
  /**
   * BHP-1150/1151: Hetzner's server-type category. Drives the picker's second
   * step, replacing shared/dedicated: a finer, cleaner split
   * (general_purpose == dedicated).
   */
  category: Category;
  /**
   * BHP-1154: CPU architecture. The third picker step: within a category it
   * splits "Intel / AMD" (x86: cx/cpx/ccx) from "ARM" (cax) — the axis category
   * does not capture (cost_optimized mixes cx and cax).
   */
  architecture: Architecture;
}

/**
 * BHP-1151: the three Hetzner categories the customer chooses between, from the
 * type's `category`. `general_purpose` is the dedicated line; `cost_optimized`
 * (cx/cax) and `regular_purpose` (cpx) are shared.
 */
export type Category = 'cost_optimized' | 'regular_purpose' | 'general_purpose';

/**
 * BHP-1154: CPU architecture, mirroring Hetzner's own `architecture` field.
 * `x86` covers both Intel (cx) and AMD (cpx/ccx); `arm` is the Ampere line (cax).
 */
export type Architecture = 'x86' | 'arm';

/**
 * BHP-1147: the three locations the platform offers (BHP-1146 fixed this set).
 * The customer picks one first; the picker then asks that location's
 * availability. Ordered as we want them shown.
 */
export const LOCATIONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'nbg1', label: 'Nürnberg' },
  { code: 'fsn1', label: 'Falkenstein' },
  { code: 'hel1', label: 'Helsinki' }
];

/**
 * BHP-1151: the category step, in display order. Cost optimized first — the
 * cheapest, common case — then regular, then the dedicated line.
 */
export const CATEGORIES: ReadonlyArray<{ value: Category; label: string }> = [
  { value: 'cost_optimized', label: 'Cost optimized' },
  { value: 'regular_purpose', label: 'Regular purpose' },
  { value: 'general_purpose', label: 'General purpose' }
];

/**
 * BHP-1154: the architecture step, in display order. Intel/AMD (x86) first — the
 * common case; ARM (cax) is the exception, only present under cost_optimized.
 */
export const ARCHITECTURES: ReadonlyArray<{
  value: Architecture;
  label: string;
}> = [
  { value: 'x86', label: 'Intel / AMD' },
  { value: 'arm', label: 'ARM' }
];

/** A Hetzner project the customer can create a server in (`GET /api/hetzner-accounts`). */
export interface HetznerProject {
  uuid: string;
  projectName: string;
}

/** The body of `POST /api/vps` (BHP-1130). */
export interface VpsCreateInput {
  hostname: string;
  hetznerAccountUuid: string;
  serverType: string;
  location: string;
  customerUuid: string;
}

/**
 * Mirror of the backend hostname rule (BHP-1130 CreateVpsDto) so a bad hostname
 * is caught before the Hetzner call, not after. Kept in sync deliberately: the
 * backend 422 is still the backstop, this only spares the round trip.
 */
const HOSTNAME_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
export const HOSTNAME_MAX_LENGTH = 63;

/** Null when the hostname is acceptable, otherwise the reason to show the user. */
export function hostnameError(hostname: string): string | null {
  if (hostname === '') {
    return 'Hostname is required.';
  }
  if (hostname.length > HOSTNAME_MAX_LENGTH) {
    return `Hostname must not exceed ${HOSTNAME_MAX_LENGTH} characters.`;
  }
  if (!HOSTNAME_PATTERN.test(hostname)) {
    return 'Hostname must be lowercase alphanumeric, may contain hyphens, and must not start or end with a hyphen.';
  }
  return null;
}

const PRICE_FORMATTER = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR'
});

/** The monthly price as net EUR, e.g. "€ 9,00". Backend prices are always EUR. */
export function formatMonthlyPrice(amount: number): string {
  return PRICE_FORMATTER.format(amount);
}

/**
 * BHP-1151: the types for one category, in display order. The backend orders by
 * price, which interleaves the families (cx/cpx/cax); sort by name so a family
 * stays together and grows in size, left to right.
 */
export function typesForCategory(
  serverTypes: ServerTypeOption[],
  category: Category | ''
): ServerTypeOption[] {
  return serverTypes
    .filter((type) => type.category === category)
    .sort((a, b) =>
      a.serverType.localeCompare(b.serverType, undefined, { numeric: true })
    );
}

/**
 * BHP-1154: which architectures actually occur among a category's types, so the
 * picker can offer the "Intel / AMD" / "ARM" chips and disable the one with no
 * types (ARM is absent under regular/general purpose).
 */
export function architecturesInCategory(
  serverTypes: ServerTypeOption[],
  category: Category | ''
): Set<Architecture> {
  return new Set(
    typesForCategory(serverTypes, category).map((type) => type.architecture)
  );
}

/**
 * BHP-1154: the choosable types for one category AND architecture, in display
 * order. Empty until both are chosen — the picker reveals the type grid only
 * after the architecture step.
 */
export function typesForCategoryAndArchitecture(
  serverTypes: ServerTypeOption[],
  category: Category | '',
  architecture: Architecture | ''
): ServerTypeOption[] {
  if (architecture === '') {
    return [];
  }
  return typesForCategory(serverTypes, category).filter(
    (type) => type.architecture === architecture
  );
}
